<template>
  <div class="p-2 bg-gray-200 dark:bg-gray-900">
    <tallyDetailsModal
      v-if="selected"
      :detail="selected"
      :startDate="startDate"
      :endDate="endDate"
    />
    <div class="h-full p-2 bg-gray-50 dark:bg-gray-800 rounded shadow-sm">
      <!-- Skeleton UI when loading -->
      <div v-if="load && !List.length" class="space-y-4 animate-pulse">
        <!-- Skeleton header -->
        <div class="h-16 bg-gray-300 dark:bg-gray-700 rounded"></div>

        <!-- Skeleton rows -->
        <div v-for="n in 10" :key="n" class="h-12 bg-gray-200 dark:bg-gray-600 rounded"></div>
      </div>

      <!-- Loading overlay when refreshing -->
      <div
        v-if="load && List.length"
        class="absolute inset-0 flex justify-center items-center bg-gray-200 dark:bg-gray-800 bg-opacity-75 z-50"
      >
        <div class="flex flex-col items-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"
          ></div>
          <span class="mt-3 text-gray-600 dark:text-gray-400"
            >Güncelleniyor...</span
          >
        </div>
      </div>

      <!-- Sortable Headers Above Table -->
      <div class="mb-2 bg-white dark:bg-gray-900 rounded shadow-sm p-3 flex gap-2 flex-wrap">
        <button
          v-for="col in columns.filter(c => c.sortable)"
          :key="col.key"
          @click="handleSort(col.key)"
          class="px-3 py-2 text-sm font-medium rounded transition-all flex items-center gap-2"
          :class="sortColumn === col.key
            ? 'bg-blue-500 text-white shadow-md'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'"
        >
          <span>{{ col.label }}</span>
          <i
            v-if="sortColumn === col.key && sortDirection === 'asc'"
            class="fas fa-sort-up"
          ></i>
          <i
            v-else-if="sortColumn === col.key && sortDirection === 'desc'"
            class="fas fa-sort-down"
          ></i>
          <i
            v-else
            class="fas fa-sort opacity-50"
          ></i>
        </button>
      </div>

      <!-- Empty State -->
      <div
        v-if="isTableReady && List.length === 0"
        class="bg-white dark:bg-gray-900 rounded-lg shadow-sm p-8 text-left"
      >
        <!-- Loading State -->
        <div v-if="load" class="flex items-center gap-3">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p class="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>

        <!-- Empty Result -->
        <div v-else>
          <div class="flex items-start gap-3 mb-4">
            <i class="fas fa-info-circle text-2xl text-blue-500"></i>
            <div>
              <p class="font-medium text-gray-900 dark:text-gray-100 mb-1">Personel bulunamadı</p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Personel başka bir şirkete kayıtlı olabilir.</p>
            </div>
          </div>
          <button
            @click="clearSearch"
            class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded transition-colors flex items-center gap-2"
          >
            <i class="fas fa-times"></i>
            <span>Aramayı Temizle</span>
          </button>
        </div>
      </div>

      <!-- Table -->
      <LyrausTable
        v-if="isTableReady && !load && List.length > 0"
        :key="tableKey"
        :columns="columns"
        :data="List"
        :isLoading="load"
        :alternateRowBg="['white', 'gray-50']"
        :containerClass="'p-2 bg-white dark:bg-gray-900 rounded-lg shadow-md border border-gray-200 dark:border-gray-700'"
        :thClass="'bg-gradient-to-b from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 text-gray-700 dark:text-gray-200 font-semibold text-sm py-3 px-3 border-b-2 border-gray-300 dark:border-gray-600 whitespace-pre-line text-center'"
        :tdClass="'whitespace-nowrap border-b border-gray-200 dark:border-gray-700 text-sm py-3 px-3 text-gray-800 dark:text-gray-200'"
        :trClass="'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-all duration-150'"
        :stickyLeft="['fullName', 'tc']"
        :buttonsColumn="{ label: 'Detaylı Rapor', width: 120 }"
        :batchOperations="true"
        :searchBar="true"
        :clickableRows="true"
        @row-click="showDetails"
        @update-search-value="handleSearchValue"
        :handleSearch="handleSearch"
      >
        <template v-slot:searchBar> <SearchInput :value="search" :isFull="true" @searcing="(val) => {handleSearchValue(val); handleSearch();}" /> </template>

        <template v-slot:colButtons="{ item }">
          <div class="flex items-center justify-end px-2">
            <button
              @click.stop="showDetails(item)"
              :disabled="isButtonLoading(item, 'detail-button')"
              class="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Detaylı raporu görüntüle"
            >
              <i v-if="isButtonLoading(item, 'detail-button')" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-chart-bar"></i>
              <span>{{ isButtonLoading(item, 'detail-button') ? 'Yükleniyor...' : 'Detay' }}</span>
            </button>
          </div>
        </template>
        <template v-slot:batchOperations>
          <form
            @submit.prevent="getAll"
            class="p-2 flex items-center justify-between bg-white dark:bg-gray-900 text-black dark:text-gray-200 border-gray-200 border-b border-t border-opacity-30"
          >
            <div class="flex-grow flex items-center mr-2">
              <!--
                 :selectedMonth="selectedMonth"
                :selectedYear="selectedYear"
              -->
              <SelectMonth
                :loading="load"
                @update:month="handleMonthUpdate"
                @update:year="handleYearUpdate"
                :model-month="selectedMonth"
                :model-year="selectedYear"
              />
            </div>
            <div class="w-full">
              <asyncBtn
                icon="fas fa-search"
                text="Ara"
                loadTitle="Yükleniyor"
                class="w-full md:w-32"
                size="py-2"
              />
            </div>
            <div class="w-full ml-2">
              <asyncBtn
                icon="fas fa-file-excel"
                text="Excel Çıktısı Al"
                loadTitle="Yükleniyor"
                :loadState="load"
                class="w-full md:w-40 bg-green-700"
                size="py-2 px-2"
                type="button"
                @click="excelExport"
                :disabled="!List.length"
              />
            </div>
          </form>
        </template>
        <template v-slot:rowButtons="{ item, columnKey }">
          <div
            v-if="
              ![
                'fullName',
                'tc',
                'workingDays',
                'absentDays',
                'totalKm',
                'adrOrders',
                'allOrders',
              ].includes(columnKey)
            "
          >
            <template
              v-if="
                item[columnKey]?.workingStatus !== undefined &&
                item[columnKey]?.workingStatus !== null
              "
            >
              <button
                v-if="Number(item[columnKey]?.workingStatus) === 1"
                @click.stop="showDayDetails(item, columnKey)"
                :disabled="isButtonLoading(item, columnKey)"
                class="flex items-center justify-center w-full h-10 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                :title="`${item.fullName} - ${item[columnKey]?.date} - Çalıştı`"
              >
                <i v-if="isButtonLoading(item, columnKey)" class="fas fa-spinner fa-spin text-gray-400"></i>
                <span v-else class="text-green-600 dark:text-green-400 font-semibold text-xl">✓</span>
              </button>
              <button
                v-else-if="Number(item[columnKey]?.workingStatus) === 0"
                @click.stop="showDayDetails(item, columnKey)"
                :disabled="isButtonLoading(item, columnKey)"
                class="flex items-center justify-center w-full h-10 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                :title="`${item.fullName} - ${item[columnKey]?.date} - Gelmedi`"
              >
                <i v-if="isButtonLoading(item, columnKey)" class="fas fa-spinner fa-spin text-gray-400"></i>
                <span v-else class="text-red-600 dark:text-red-400 font-semibold text-xl">✗</span>
              </button>
            </template>
            <span v-else class="text-gray-400">-</span>
          </div>
        </template>
      </LyrausTable>
    </div>
  </div>
</template>

<script>
import { saveAs } from "file-saver";
import { LyrausTable } from "lyraus-ui";
import * as XLSX from "xlsx";
import axios from "axios";
import moment from "moment";

import diffInput from "@/components/general/diffInput.vue";
import asyncBtn from "@/components/general/asyncBtn.vue";
import { driverTally } from "@/networking/urlmanager";
import tallyDetailsModal from "./components/tallyDetailsModal.vue";
import SelectMonth from "@/components/filters/selectMonth.vue";
import SearchInput from "@/components/general/search-input.vue";

export default {
  name: "driverTally",
  components: {
    diffInput,
    asyncBtn,
    tallyDetailsModal,
    SelectMonth,
    SearchInput
  },
  data() {
    return {
      load: true,
      columns: [],
      List: [],
      realList: [],
      startDate: undefined,
      endDate: undefined,
      selected: undefined,
      selectedMonth: new Date().getMonth(),
      selectedYear: new Date().getFullYear(),
      search: "",
      tableKey: 0,
      isTableReady: false,
      sortColumn: null,
      sortDirection: 'asc',
      loadingItemTc: null, // Hangi item için loading olduğunu track et
      loadingColumnKey: null, // Hangi sütun için loading olduğunu track et
    };
  },
  methods: {
    showDetails(item) {
      this.loadingItemTc = item.tc;
      this.loadingColumnKey = 'detail-button';
      this.selected = item;
      setTimeout(() => {
        this.$modal.show("tally-details-modal");
        // Modal açıldıktan sonra loading'i kapat (modal kendi loading'ini gösterecek)
        setTimeout(() => {
          this.loadingItemTc = null;
          this.loadingColumnKey = null;
        }, 100);
      }, 50);
    },
    showDayDetails(item, columnKey) {
      // Günlük detayları göster
      const dayData = item[columnKey];
      if (!dayData || !dayData.id) {
        this.$swal({
          icon: 'info',
          title: 'Bilgi',
          text: 'Bu gün için detay bulunmuyor.',
        });
        return;
      }

      this.loadingItemTc = item.tc;
      this.loadingColumnKey = columnKey;

      // Tüm detayları modal ile göster
      this.selected = {
        ...item,
        selectedDate: dayData.date,
        selectedDateKey: columnKey
      };

      setTimeout(() => {
        this.$modal.show("tally-details-modal");
        // Modal açıldıktan sonra loading'i kapat
        setTimeout(() => {
          this.loadingItemTc = null;
          this.loadingColumnKey = null;
        }, 100);
      }, 50);
    },
    isButtonLoading(item, columnKey) {
      return this.loadingItemTc === item.tc && this.loadingColumnKey === columnKey;
    },
    handleSearchValue(val) {
      this.search = val;
    },
    clearSearch() {
      this.search = '';
      this.getAll();
    },
    handleSearch() {
      console.log('🔎 handleSearch çağrıldı. Search:', this.search);

      // Eğer search boşsa lokal filtreleme yap
      if (!this.search || !this.search.trim()) {
        console.log('ℹ️ Search boş, lokal listeye dönülüyor');
        this.List = this.realList;
        this.applySorting();
        return;
      }

      // Search varsa önce lokal filtreleme yap
      const searchTerm = this.search.toLowerCase();
      this.List = this.realList.filter(
        (item) =>
          item.fullName.toLowerCase().includes(searchTerm) ||
          item.tc.includes(searchTerm)
      );
      this.applySorting();

      console.log(`📊 Lokal filtreleme sonucu: ${this.List.length} kayıt bulundu`);

      // Eğer sonuç bulunamadıysa, API'den alt şirket kontrolü yap
      if (this.List.length === 0) {
        console.log('⚠️ Lokal listede sonuç yok, API çağrılıyor...');
        this.getAll();
      }
    },
    handleSort(column) {
      if (this.sortColumn === column) {
        // Aynı kolona tıklanırsa direction değiştir
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Yeni kolona tıklanırsa asc ile başla
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      this.applySorting();
    },
    applySorting() {
      if (!this.sortColumn) return;

      this.List.sort((a, b) => {
        let aVal = a[this.sortColumn];
        let bVal = b[this.sortColumn];

        // String değerler için
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        // Sayı değerler için
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }

        // String karşılaştırma
        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    },
    getAllDatesInMonth(year, month) {
      const startDate = moment([year, month]).startOf("month");
      const endDate = moment(startDate).endOf("month");
      const dates = [];

      let currentDate = startDate.clone();
      while (currentDate <= endDate) {
        dates.push(currentDate.format("YYYY-MM-DD"));
        currentDate.add(1, "days");
      }

      return dates;
    },
    transformData(inputData) {
      const allDates = this.getAllDatesInMonth(
        this.selectedYear,
        this.selectedMonth
      );

      this.columns = [
        { key: "fullName", label: "Personel Adı", width: 200, sortable: true },
        { key: "tc", label: "TC Kimlik No", width: 140, sortable: true },
        { key: "workingDays", label: "Çalıştığı Gün", width: 130, sortable: true },
        { key: "absentDays", label: "Devamsızlık", width: 130, sortable: true },
        { key: "totalKm", label: "Toplam KM", width: 120, sortable: true },
        { key: "adrOrders", label: "ADR Seferleri", width: 160, sortable: true },
        { key: "allOrders", label: "Toplam Sefer", width: 130, sortable: true },
        ...allDates.map((date) => {
          const dayName = moment(date).format('ddd'); // Mon, Tue, Wed...
          const dayNumber = date.split("-")[2];
          return {
            key: date.replace(/-/g, ""),
            label: `${dayNumber}\n${dayName}`,
            fullDate: date,
            width: 90,
            dontDisplay: true,
          };
        }),
      ];

      if (!inputData || !inputData.length) {
        this.List = this.realList = [];
        return;
      }

      this.List = this.realList = inputData.map((person) => {
        const result = {
          fullName: `${person.name} ${person.surname}`,
          tc: person.tc,
          totalKm: person.totalKm || 0,
          adrOrders: person.adrOrders || 0,
          allOrders: person.allOrders || 0,
        };

        const tallyMap = new Map(
          person.tally.map((record) => [
            moment(record.date).format("YYYY-MM-DD"),
            record,
          ])
        );

        // Çalışma günü ve gelmeme günü sayaçları
        let workingDays = 0;
        let absentDays = 0;

        allDates.forEach((date) => {
          const dateKey = date.replace(/-/g, "");
          const record = tallyMap.get(date);

          if (record) {
            result[dateKey] = {
              id: record.id,
              date: record.date,
              workingStatus: record.workingStatus,
            };

            // İstatistik hesaplama
            if (record.workingStatus === 1) {
              workingDays++;
            } else if (record.workingStatus === 0) {
              absentDays++;
            }
          } else {
            result[dateKey] = {
              id: null,
              date: date,
              workingStatus: undefined,
            };
          }
        });

        // İstatistikleri ekle
        result.workingDays = workingDays;
        result.absentDays = absentDays;

        return result;
      });
    },
    async getAll() {
      this.load = true;
      this.List = [];
      try {
        const params = {
          startDate: moment(this.startDate)
            .utc()
            .add(1, "day")
            .startOf("day")
            .format("YYYY-MM-DD HH:mm:ss"),
          endDate: moment(this.endDate)
            .utc()
            .endOf("day")
            .format("YYYY-MM-DD HH:mm:ss"),
        };

        // Eğer search varsa parametreye ekle
        if (this.search && this.search.trim()) {
          params.search = this.search.trim();
          console.log('🔍 Search parametresi eklendi:', params.search);
        }

        console.log('📤 API isteği gönderiliyor. Parametreler:', params);

        const { data } = await axios.get(driverTally.getAll, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        console.log('📥 API Response:', data);
        console.log('⚠️ Warning var mı?', data.warning ? 'EVET' : 'HAYIR');

        // Alt şirket uyarısı kontrolü
        if (data.warning && data.warning.type === 'SUBCONTRACTOR_DRIVER') {
          console.log('🚨 Alt şirket uyarısı gösteriliyor:', data.warning);
          const driver = data.warning.driver;
          this.$swal({
            icon: 'warning',
            title: '⚠️ Alt Şirket Sürücüsü',
            html: `
              <div class="text-left" style="font-size: 14px;">
                <div class="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p class="text-gray-700" style="line-height: 1.6;">
                    Bu sürücü <strong>GÜNEY EKSPORT'a</strong> bağlı değil.<br>
                    <strong class="text-yellow-700">"${driver.company}"</strong> alt şirketine bağlı olduğu için tally kayıtlarında görünmüyor.
                  </p>
                </div>
                <div class="bg-gray-50 p-4 rounded border border-gray-200">
                  <h4 class="font-bold mb-3 text-gray-800 border-b pb-2">Sürücü Bilgileri</h4>
                  <div class="space-y-2">
                    <p class="mb-2"><strong class="text-gray-600">Ad Soyad:</strong> <span class="text-gray-900">${driver.name}</span></p>
                    <p class="mb-2"><strong class="text-gray-600">TC Kimlik:</strong> <span class="text-gray-900">${driver.tc}</span></p>
                    <p class="mb-2"><strong class="text-gray-600">Bağlı Şirket:</strong> <span class="text-blue-700">${driver.company}</span></p>
                    <p class="mb-2"><strong class="text-gray-600">Çalışma Durumu:</strong> <span class="text-green-600">${driver.status}</span></p>
                    ${driver.phone ? `<p class="mb-2"><strong class="text-gray-600">Telefon:</strong> <span class="text-gray-900">${driver.phone.replace('MOBİL 1: ', '')}</span></p>` : ''}
                  </div>
                </div>
              </div>
            `,
            confirmButtonText: 'Anladım',
            confirmButtonColor: '#f59e0b',
            width: '600px'
          });
        }

        this.transformData(data.data);
        this.tableKey++;
        this.isTableReady = true;
      } catch (err) {
        this.errorBox(err.response);
      } finally {
        this.load = false;
      }
    },
    async excelExport() {
      this.load = true; // Added loading state for Excel export
      try {
        const workbook = XLSX.utils.book_new();

        const headers = [
          "Ad Soyad",
          "TC Kimlik",
          "Çalışma Günü",
          "Gelmeme Günü",
          "Toplam Km",
          "ADR Sefer",
          "Sefer Sayısı",
        ];

        const excludedColumns = [
          "fullName",
          "tc",
          "workingDays",
          "absentDays",
          "totalKm",
          "adrOrders",
          "allOrders",
        ];

        this.columns
          .filter((col) => !excludedColumns.includes(col.key))
          .forEach((col) => {
            headers.push(col.label.replace('\n', ' ')); // Remove line break for Excel
          });

        const rows = this.List.map((item) => {
          const row = [
            item.fullName,
            item.tc,
            item.workingDays,
            item.absentDays,
            item.totalKm,
            item.adrOrders,
            item.allOrders,
          ];

          this.columns
            .filter((col) => !excludedColumns.includes(col.key))
            .forEach((col) => {
              const status = item[col.key]?.workingStatus;
              if (status === undefined || status === null) {
                row.push("-");
              } else {
                row.push(status === 1 ? "Çalıştı" : "Çalışmadı");
              }
            });
          return row;
        });

        const detail = [headers, ...rows];

        const worksheet = XLSX.utils.aoa_to_sheet(detail);
        XLSX.utils.book_append_sheet(workbook, worksheet, "driverTally");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const data = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(data, "driver-tally.xlsx");
      } catch (error) {
        this.errorBox(error);
      } finally {
        this.load = false; // Reset loading state after export is complete
      }
    },
    getThisMonthsStartDate() {
      return moment().startOf("month").utc().format("YYYY-MM-DDTHH:mm");
    },
    getThisMonthsEndDate() {
      return moment().endOf("month").utc().format("YYYY-MM-DDTHH:mm");
    },
    handleMonthUpdate(month, year) {
      this.selectedMonth = month;
      this.selectedYear = year;
      this.startDate = moment()
        .month(month)
        .year(year)
        .startOf("month")
        .format("YYYY-MM-DDTHH:mm");
      this.endDate = moment()
        .month(month)
        .year(year)
        .endOf("month")
        .format("YYYY-MM-DDTHH:mm");
    },
    handleYearUpdate(month, year) {
      this.selectedMonth = month;
      this.selectedYear = year;
      this.startDate = moment()
        .month(month)
        .year(year)
        .startOf("month")
        .format("YYYY-MM-DDTHH:mm");
      this.endDate = moment()
        .year(year)
        .month(month)
        .endOf("month")
        .format("YYYY-MM-DDTHH:mm");
    },
  },
  created() {
    this.startDate = this.startDate ?? this.getThisMonthsStartDate();
    this.endDate = this.endDate ?? this.getThisMonthsEndDate();
    this.getAll();
  },
};
</script>
